import { useState } from 'react';

import { Input } from '@/components/ui/Input';
import { Box, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { formatCents, sanitizeDollars, toDollarsString } from './usage-format';

export const isValidUsageAmount = (
  cents: number | null,
  minCents: number,
  maxCents: number
): boolean => cents != null && cents >= minCents && cents <= maxCents;

type UsageAmountInputProps = {
  valueCents: number | null;
  onValueCentsChange: (cents: number | null) => void;
  minCents: number;
  maxCents: number;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
};

export const UsageAmountInput = ({
  valueCents,
  onValueCentsChange,
  minCents,
  maxCents,
  disabled,
  className,
  placeholder = '10',
}: UsageAmountInputProps) => {
  const [dollarsStr, setDollarsStr] = useState(
    valueCents != null ? toDollarsString(valueCents) : ''
  );

  const handleInput = (raw: string) => {
    const sanitized = sanitizeDollars(raw);
    setDollarsStr(sanitized);
    const dollars = sanitized === '' ? null : parseFloat(sanitized);
    onValueCentsChange(
      dollars != null && Number.isFinite(dollars)
        ? Math.round(dollars * 100)
        : null
    );
  };

  const outOfBounds =
    valueCents != null && !isValidUsageAmount(valueCents, minCents, maxCents);

  return (
    <YStack className={cn('gap-1.5', className)}>
      <Box className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-muted pointer-events-none">
          $
        </span>
        <Input
          value={dollarsStr}
          onChange={(e) => handleInput(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-7"
        />
      </Box>
      {outOfBounds && (
        <span className="text-xs text-destructive">
          Enter between {formatCents(minCents)} and {formatCents(maxCents)}.
        </span>
      )}
    </YStack>
  );
};
